import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '~app/auth/auth.service';
import { InfluxFilters } from '../influx.service';
import { InfluxMetadataService, Scalar, ScalarObject } from './influx-metadata.service';
import { environment } from '~environments/environment';

@Injectable()
export class InfluxMetadataMisonetService extends InfluxMetadataService {
  private cache: Map<string, Promise<ScalarObject[]>> = new Map();

  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
  ) {
    super();
  }

  protected get specialFields(): Map<string, Map<Scalar, Scalar>> {
    return new Map([
      ['_field', new Map([
        // [0, 'alku'],
        // [1, 'loppu'],
        [2, 'min'],
        [3, 'max'],
        [4, 'keskiarvo'],
        // [5, 'koko'],
        [6, 'laskuri'],
        // [7, 'käyntikerta'],
        // [8, 'loppu-alku'],
        // [9, 'ka_lkm'],
      ])]
    ]);
  }

  protected load(field: string, filters: ScalarObject): Promise<ScalarObject[]> {
    const valvomo = filters.valvomo as string;
    if (!this.cache.has(valvomo)) {
      const url = new URL('influx/registry/miso', environment.apigwURL);
      if (valvomo) {
        url.pathname += `/${encodeURIComponent(valvomo)}`;
      }
      this.cache.set(valvomo, this.http.get(url.toString()).toPromise() as Promise<ScalarObject[]>);
    }
    return this.cache.get(valvomo);
  }

  protected async loadTimeline(
    bucket: string,
    measurement: string,
    filters: InfluxFilters = {},
  ): Promise<[string, number | string | boolean][]> {
    //// filters already validated
    const queryParams = new URLSearchParams(Object.entries(filters));
    const url = new URL(`influx/buckets/${bucket}/measurements/${measurement}?${queryParams}`, environment.apigwURL);
    return this.http.get(url.toString()).toPromise() as Promise<[string, number | string | boolean][]>;
  }

  protected async distinctValues(field: string, filters: ScalarObject, asUnits: boolean = false): Promise<Set<Scalar>> {
    if (this.specialFields.has(field)) {
      return new Set(this.specialFields.get(field).values());
    }

    const data = await this.load(field, filters);
    return new Set(
      data
        .filter(row => {
          return Object.entries(filters).every(([key, value]) => this.specialFields.has(key) || row[key] === value);
        })
        .map(row => row[field])
    );
  }

  public async resolveTimelineFilters(filters: InfluxFilters): Promise<InfluxFilters> {
    const underscoredFilters = Object.entries(filters).filter(([key, value]) => key.startsWith('_'));
    const nonUnderscoredFilters = Object.entries(filters).filter(([key, value]) => !key.startsWith('_'));

    const data = await this.load(null, filters);
    const match = data.find(row => nonUnderscoredFilters.every(([key, value]) => row[key]?.toString() === value));
    if (!match) {
      throw new Error('invalid filter set');
    }

    return {
      rivi: match.rivi.toString(),
      valvomo: match.valvomo.toString(),
      ...Object.fromEntries(underscoredFilters),
      // resolve _field back into a number
      _field: Array.from(this.specialFields.get('_field').entries()).find(([key, value]) => value === filters._field)[0].toString(),
    };
  }

  public resolveWindowFunction(name: string): InfluxFilters['_windowFunction'] | undefined {
    switch (name) {
      case 'keskiarvo':
        return 'mean';
      case 'laskuri':
        return 'sum';
    }
    return super.resolveWindowFunction(name);
  }
}

